import { JsonRestClient } from "../../api/rest_interceptor";
import { 
    DOWNLOAD_TENANTS_REPORT,
    DOWNLOAD_TENANT_TRANSACTIONS_REPORT,
    DOWNLOAD_TRANSACTIONS,
    GENERATE_REPORTS,
    GENERATE_MULTI_PROPERTY_REPORTS,
    GENERATE_ARREARS_REPORTS,
    GENERATE_EXPENSES_REPORTS,
    GENERATE_MONTH_ON_MONTH_REPORTS,
    GENERATE_YEAR_ON_YEAR_REPORTS,
    GENERATE_PROPERTY_GROUPING_REPORT,
    GENERATE_DELETED_TENANTS_REPORT
} from "../actions/actionTypes";
import { updateActionErrors } from "./updateErrors";
import fileDownload from "js-file-download";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Toast } from "@capacitor/toast";
import writeBlob from "capacitor-blob-writer";


const getFileType = (outputFormat) =>{
    let fileType = "csv"
    if (outputFormat == "pdf"){
        fileType = outputFormat;
    }else if (outputFormat == "excel"){
        fileType = "xlsx";
    }
    return fileType;
}

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const dataUrl = reader.result || '';
        const base64 = String(dataUrl).split(',')[1] || '';
        resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const inferMimeType = (fileName) => {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
        case 'pdf': return 'application/pdf';
        case 'csv': return 'text/csv';
        case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'xls': return 'application/vnd.ms-excel';
        case 'png': return 'image/png';
        case 'jpg':
        case 'jpeg': return 'image/jpeg';
        default: return 'application/octet-stream';
    }
}

async function handleNativeDownload(blob, fileName) {
    try {
        const isAndroid = Capacitor.getPlatform && Capacitor.getPlatform() === 'android';
        const mimeType = inferMimeType(fileName);
        if (isAndroid) {
            await writeBlob({
                path: `Download/${fileName}`,
                blob,
                directory: Directory.ExternalStorage,
                recursive: true,
            });
            try { await Toast.show({ text: `Saved to Downloads/${fileName}` }); } catch (_) {}
        } else {
            const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
            const base64Data = await blobToBase64(typedBlob);
            await Filesystem.writeFile({
                path: `${fileName}`,
                data: base64Data,
                directory: Directory.Documents,
                recursive: true,
            });
            try {
                const uri = await Filesystem.getUri({ path: `${fileName}`, directory: Directory.Documents });
                if (uri && uri.uri) {
                    await Share.share({ title: fileName, url: uri.uri });
                }
            } catch (_) {}
        }
    } catch (err) {
        console.log('Unable to save file', err);
    }
}

const downloadOrSave = async (data, fileName) => {
    const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
    if (isNative) {
        const mimeType = inferMimeType(fileName);
        const dataBlob = (data instanceof Blob && data.type)
            ? data
            : new Blob([data], { type: mimeType });
        await handleNativeDownload(dataBlob, fileName);
    } else {
        fileDownload(data, fileName);
    }
}


const  tenantTransactionsReportMiddleware = async (action, storeAPI) => {
    let tenantId = action.payload.tenantId;
        let requestPayload = {
            tenant_id: tenantId,
            outputFormat: action.payload.outputFormat
        };
        try {
            const response = await JsonRestClient.post('api/tenant_report/generate', requestPayload,
                { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
            );
            const blob = response.data;
                const { outputFormat } = action.payload;
                if (outputFormat != "json"){
                    const tenant = storeAPI.getState().tenants[tenantId];
                    let fileName = `tenantTransactions-${tenantId}.${getFileType(outputFormat)}`;
                    if(tenant){
                        fileName = `${tenant.firstName} ${tenant.lastName} Report.${getFileType(outputFormat)}`;
                    }
                    await downloadOrSave(blob, fileName);
                }else{
                    action.data = blob;
                }
        } catch (error) {
            console.error(error);
            action = updateActionErrors(action, error)
        }
        return action;
}

const downloadTenantsReportMiddleware = async (action, storeAPI) => {
    let filters = action.payload;
    const data = {
        property_ids: filters.propertyIds,
        max_balance: filters.maxBalance,
        min_balance: filters.minBalance,
        search_text: filters.searchText,
        archived: filters.archived,
        days_to_lease_expiry: filters.daysToLeaseExpiry,
        tenant_ids: filters.tenantIds,
        sort_field: filters.sortField,
        sort_order: filters.sortOrder
    };
    try{
        const response = await JsonRestClient.post('api/tenants/download', data, { responseType: 'blob' });
        const blob = response.data;
        let fileName = `Tenants.pdf`;
        await downloadOrSave(blob, fileName);
    }catch(error){
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}


const propertyReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_id: reportFilters.propertyId,
        outputFormat: reportFilters.outputFormat,
        start_date: reportFilters.startDate,
        end_date: reportFilters.endDate,
        report_settings: reportFilters.reportSettings
    };
    try {
        const response = await JsonRestClient.post('api/reports/generate', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            const propertyName = storeAPI.getState().properties[reportFilters.propertyId].propertyName
            let fileName = `${propertyName}-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const multiPropertyReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_ids: reportFilters.propertyIds,
        outputFormat: reportFilters.outputFormat,
        start_date: reportFilters.startDate,
        end_date: reportFilters.endDate,
        report_settings: reportFilters.reportSettings,
        all_properties_selected: Boolean(reportFilters.allPropertiesSelected)
    };
    try {
        const response = await JsonRestClient.post('api/reports/multi-property-statement', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            let fileName = `MultiProperty-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const arrearsReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_ids: reportFilters.propertyIds,
        outputFormat: reportFilters.outputFormat,
        start_date: reportFilters.startDate,
        end_date: reportFilters.endDate,
        report_settings: reportFilters.reportSettings
    };
    try {
        const response = await JsonRestClient.post('api/reports/arrears', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            let propertyId = reportFilters.propertyIds.length == 1 ? reportFilters.propertyIds[0] : "all";
            const propertyName = propertyId !== "all"? storeAPI.getState().properties[propertyId].propertyName: "All Properties";
            let fileName = `${propertyName}-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const expensesReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_id: reportFilters.propertyId,
        outputFormat: reportFilters.outputFormat,
        start_date: reportFilters.startDate,
        end_date: reportFilters.endDate
    };
    try {
        const response = await JsonRestClient.post('api/reports/expenses', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            const propertyName = storeAPI.getState().properties[reportFilters.propertyId].propertyName
            let fileName = `${propertyName}-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const monthOnMonthReportsMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_id: reportFilters.propertyId,
        outputFormat: reportFilters.outputFormat,
        start_year_month: reportFilters.startDate,
        end_year_month: reportFilters.endDate,
        chartGraph: reportFilters.chartGraph
    };
    try {
        const response = await JsonRestClient.post('api/reports/month-on-month', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            const propertyName = storeAPI.getState().properties[reportFilters.propertyId].propertyName
            let fileName = `${propertyName}-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const yearOnYearReportsMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_id: reportFilters.propertyId,
        outputFormat: reportFilters.outputFormat,
        start_year: reportFilters.startDate,
        end_year: reportFilters.endDate,
        chartGraph: reportFilters.chartGraph
    };
    try {
        const response = await JsonRestClient.post('api/reports/year-on-year', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            const propertyName = storeAPI.getState().properties[reportFilters.propertyId].propertyName
            let fileName = `${propertyName}-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const generatePropertyGroupingReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        grouping_ids: reportFilters.groupingIds,
        outputFormat: reportFilters.outputFormat,
        start_date: reportFilters.startDate,
        end_date: reportFilters.endDate,
        chartGraph: reportFilters.chartGraph
    };
    try{
        const response = await JsonRestClient.post('api/reports/property-grouping', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            let fileName = `PropertyGrouping-${reportFilters.startDate}_to_${reportFilters.endDate}Report.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}

const generateDeletedTenantsReportMiddleware = async (action, storeAPI) => {
    let reportFilters = action.payload;
    let data = {
        property_id: reportFilters.propertyId,
        tenant_ids: reportFilters.tenantIds,
        outputFormat: reportFilters.outputFormat,
    };
    try {
        const response = await JsonRestClient.post('api/reports/deleted-tenants', data,
            { responseType: action.payload.outputFormat !== 'json' ? 'blob' : 'json' }
        );
        const blob = response.data;
        const { outputFormat } = action.payload;
        if (outputFormat != "json"){
            const propertyName = storeAPI.getState().properties[reportFilters.propertyId].propertyName;
            let fileName = `${propertyName}-DeletedTenantsReport.${getFileType(outputFormat)}`;
            await downloadOrSave(blob, fileName);
        }else{
            action.data = blob;
        }
    } catch (error) {
        console.error(error);
        action = updateActionErrors(action, error);
    }
    return action;
}


const reportsMiddleware  = storeAPI => next =>async action => {

    switch(action.type){
        case GENERATE_REPORTS:
            action = await propertyReportMiddleware(action, storeAPI);
            break;
        case GENERATE_MULTI_PROPERTY_REPORTS:
            action = await multiPropertyReportMiddleware(action, storeAPI);
            break;
        case GENERATE_ARREARS_REPORTS:
            action = await arrearsReportMiddleware(action, storeAPI);
            break;
        case GENERATE_EXPENSES_REPORTS:
            action = await expensesReportMiddleware(action, storeAPI);
            break;
        case GENERATE_MONTH_ON_MONTH_REPORTS:
            action = await monthOnMonthReportsMiddleware(action, storeAPI);
            break;
        case GENERATE_YEAR_ON_YEAR_REPORTS:
            action = await yearOnYearReportsMiddleware(action, storeAPI);
            break;
        case DOWNLOAD_TENANTS_REPORT:
            action = await downloadTenantsReportMiddleware(action, storeAPI);
            break;
        case DOWNLOAD_TENANT_TRANSACTIONS_REPORT:
            action = await tenantTransactionsReportMiddleware(action, storeAPI);
            break;
        case GENERATE_PROPERTY_GROUPING_REPORT:
            action = await generatePropertyGroupingReportMiddleware(action, storeAPI);
            break;
        case GENERATE_DELETED_TENANTS_REPORT:
            action = await generateDeletedTenantsReportMiddleware(action, storeAPI);
            break;

        // download invoices and payments in a csv
        // based of the filters of the currently displayed invoices or payments
        case DOWNLOAD_TRANSACTIONS:
            let payload = action.payload;

            let reqPayload = {
                ...payload.filters
            };
            try {
                const response = await JsonRestClient.post(`api/${payload.transactionType}/download`, reqPayload, { responseType: 'blob' });
                let fileName = `${payload.transactionType}.csv`;
                if (response.headers['content-disposition']){
                    const cd = response.headers['content-disposition'];
                    const match = cd.split("filename=")[1] || '';
                    fileName = match.replace(/^"+|"+$/g, '');
                }
                await downloadOrSave(response.data, fileName);
            } catch (error) {
                console.error(error);
                action = updateActionErrors(action, error)
            }
            break;
    }
    return next(action)
};

export {
    reportsMiddleware
}